"use client";

import type { RepeatType } from "motion/react";
import type { HTMLAttributes } from "react";

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { motion, useAnimation } from "motion/react";

interface DownvoteIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface DownvoteIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  loop?: boolean;
  animateOnMount?: boolean;
}

const DownvoteIcon = forwardRef<DownvoteIconHandle, DownvoteIconProps>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 28,
      loop = false,
      animateOnMount = false,
      ...props
    },
    ref,
  ) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => void controls.start("animate"),
        stopAnimation: () => void controls.start("normal"),
      };
    });

    useEffect(() => {
      if ((loop || animateOnMount) && !isControlledRef.current) {
        void controls.start("animate");
      }
    }, [loop, animateOnMount, controls]);

    const handleMouseEnter = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("animate");
        } else {
          onMouseEnter?.(e);
        }
      },
      [controls, onMouseEnter, loop],
    );

    const handleMouseLeave = useCallback(
      async (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current && !loop) {
          await controls.start("normal");
        } else {
          onMouseLeave?.(e);
        }
      },
      [controls, onMouseLeave, loop],
    );

    const getRepeatStyles = useCallback(() => {
      let repeatStyles: { repeat: number; repeatType: RepeatType | undefined } =
        {
          repeat: 0,
          repeatType: undefined,
        };

      if (loop) {
        repeatStyles = {
          repeat: Number.POSITIVE_INFINITY,
          repeatType: "reverse",
        };
      }

      if (animateOnMount) {
        repeatStyles = {
          repeat: 1,
          repeatType: "reverse",
        };
      }

      return repeatStyles;
    }, [loop, animateOnMount]);

    return (
      <div
        className={twMerge(className)}
        onMouseEnter={(e) => void handleMouseEnter(e)}
        onMouseLeave={(e) => void handleMouseLeave(e)}
        {...props}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={{
            normal: {
              translateX: "0px",
              translateY: "0px",
              rotate: "0deg",
            },
            animate: {
              translateX: "-1px",
              translateY: "2px",
              rotate: "-12deg",
            },
          }}
          animate={controls}
          transition={{
            type: "spring",
            stiffness: 250,
            damping: 25,
            ...getRepeatStyles(),
          }}
        >
          <path d="M17 14V2" />
          <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
        </motion.svg>
      </div>
    );
  },
);

DownvoteIcon.displayName = "DownvoteIcon";

export { DownvoteIcon };
